import { PropertyMemberAction } from "@pages/Property/PropertyPage/PropertyMembers/PropertyMemberAction";
import { getCachedApiSettings } from "@src/data-hooks/general-settings/settings.service";
import { getSingularRoleTitle } from "@src/utils/data/roles.utils";
import { getMemberVisibilityType, isMemberVisible } from "@src/utils/settings/settings.utils";
import { arrayRemove } from "firebase/firestore";
import { t } from "i18next";
import { DataTableData } from "../../../../../components/deprecated/DataTable/DataTable";
import { Popup } from "../../../../../components/deprecated/Popup/Popup";
import { PopupTemplates } from "../../../../../components/deprecated/PopupTemplates/PopupTemplates";
import { UserNameLabel } from "../../../../../components/deprecated/Text/Label/UserNameLabel";
import { Text } from "../../../../../components/deprecated/Text/Text";
import { UserImageView } from "../../../../../components/deprecated/UserImageView/UserImageView";
import { ToastUtilities } from "../../../../../libs/react-toastify/ToastUtilities";
import { ActionValidator } from "../../../../../auth/actionValidator";
import { MemberData } from "../../../Property/MemberData";
import { Role } from "../../../User/Role";
import { FirebaseDataContractorField } from "../../DataObjectField/CustomFields/FirebaseDataContractorField";
import { FirebaseDataFirebaseFileField } from "../../DataObjectField/CustomFields/FirebaseDataFirebaseFileField";
import { FirebaseDataPropertyField } from "../../DataObjectField/CustomFields/FirebaseDataPropertyField";
import { FirebaseDataUnitField } from "../../DataObjectField/CustomFields/FirebaseDataUnitField";
import { FirebaseDataUserField } from "../../DataObjectField/CustomFields/FirebaseDataUserField";
import { PropertyMemberRolesFields } from "../../DataObjectField/CustomFields/PropertyMemberFields";
import { DataObjectField } from "../../DataObjectField/DataObjectField";
import { FirebaseDataBooleanField } from "../../DataObjectField/Generic/FirebaseDataBooleanField";
import { FirebaseDataStringField } from "../../DataObjectField/Generic/FirebaseDataStringField";
import { DataObjectAction, DataObjectHelper } from "../../DataObjects/DataObjectHelper";

export class PropertyMemberDataHelper extends DataObjectHelper<MemberData> {
  id = () => "propertyMembers";
  displayName = () => t("main.propertyMembers");

  profileImage: FirebaseDataFirebaseFileField<MemberData> = new FirebaseDataFirebaseFileField<MemberData>(
    "profileImage",
    "",
    (data) => data.user.profileImage_Small,
    ""
  );
  user: FirebaseDataUserField<MemberData> = new FirebaseDataUserField("user", t("main.person"), (data) => data.user, "title");
  property: FirebaseDataPropertyField<MemberData> = new FirebaseDataPropertyField("property", t("main.property"), (data) => data.property, "home");
  admin: FirebaseDataBooleanField<MemberData> = new FirebaseDataBooleanField(
    "admin",
    t("main.superUser"),
    (data) => data.isAdmin,
    "admin_panel_settings"
  );
  roles: PropertyMemberRolesFields<MemberData> = new PropertyMemberRolesFields("roles", t("main.roles"), (data) => data.roles, "person", true);
  units: FirebaseDataUnitField<MemberData> = new FirebaseDataUnitField("units", t("main.tenant"), (data) => data.units, "group", true);
  contractorGroups: FirebaseDataContractorField<MemberData> = new FirebaseDataContractorField(
    "contractorGroups",
    t("main.contractorGroups"),
    (data) => data.contractorGroups,
    undefined,
    true
  );
  operationManager: FirebaseDataBooleanField<MemberData> = new FirebaseDataBooleanField(
    "operationManager",
    t("main.operationManager"),
    (data) => data.isOperationManager,
    "engineering"
  );
  roleTitle: FirebaseDataStringField<MemberData> = new FirebaseDataStringField("roleTitle", t("main.roleTitle"), (data) => data.roleTitle);

  allFields(): DataObjectField<any, MemberData>[] {
    return [
      this.profileImage,
      this.user,
      this.property,
      this.roles,
      this.units,
      this.contractorGroups,
      this.admin,
      this.operationManager,
      this.roleTitle,
    ];
  }

  getAllDataTableDatas(documents: MemberData[]): DataTableData[] {
    return documents.map((member, index) => {
      const tenants = member.units.map((unit) => unit.name).join(", ");
      const contractorGroups = member.contractorGroups.map((contractor) => contractor.name).join(", ");
      return {
        key: member.property.id + "-" + member.id,
        cells: {
          property:
            member.property.name !== ""
              ? member.property.name
              : {
                  element: (
                    <Text size="md" isItalic>
                      {t("main.none")}
                    </Text>
                  ),
                },
          profileImage: { element: <UserImageView size={30} user={member.user} /> },
          user: { element: <UserNameLabel user={member.user} type="paragraph" />, rawString: member.user.name },
          roles: member.roles.map((role) => getSingularRoleTitle(role.id, undefined)).join(", "),
          units:
            tenants !== ""
              ? tenants
              : {
                  element: (
                    <Text size="md" isItalic>
                      {t("main.none")}
                    </Text>
                  ),
                },
          contractorGroups:
            contractorGroups !== ""
              ? contractorGroups
              : {
                  element: (
                    <Text size="md" isItalic>
                      {t("main.none")}
                    </Text>
                  ),
                },
          roleTitle:
            member.roleTitle !== ""
              ? member.roleTitle
              : {
                  element: (
                    <Text size="md" isItalic>
                      {t("main.none")}
                    </Text>
                  ),
                },
          admin: member.isAdmin ? t("main.yes") : t("main.no"),
          operationManager: member.isOperationManager ? t("main.yes") : t("main.no"),
          actions: { element: <PropertyMemberAction member={member} property={member.property} dataCy={`table-row-action-button-${index}`} /> },
        },
      };
    });
  }

  override getActionsForData = (data: MemberData): DataObjectAction<MemberData>[] => {
    const property = data.property;
    const actions: DataObjectAction<MemberData>[] = [];
    let roleIdentifier = data.hasLandlord() ? "landlord" : data.hasEntrepreneur() ? "contractor" : data.hasTenant() ? "tenant" : undefined;

    function saveUpdate(saveData: { [key: string]: any }) {
      return ToastUtilities.presentSavingToast(data.saveUpdate(saveData));
    }

    if (roleIdentifier && ActionValidator.isAllowedToPerformAction("property_edit_role_title_" + roleIdentifier, [property])) {
      actions.push({
        id: "edit_role_title",
        title: t("main.editTitle"),
        iconColor: "var(--primary-color)",
        iconName: "badge",
        requiredConsent: (dataObjects) => {
          return new Promise((resolve) => {
            PopupTemplates.presentTextInput({
              title: t("main.editMemberTitle"),
              placeholder: t("main.editTitle"),
              onConfirm: (popupId, title) => {
                Popup.remove(popupId);
                resolve({ dataObjects, data: title });
              },
              actionTitle: t("main.confirm"),
            });
          });
        },
        performAction: (data) => {
          return saveUpdate({
            roleTitle: data,
          });
        },
      });
    }

    if (
      ActionValidator.isAllowedToPerformAction(data.isAdmin ? "property_kick_landlord" : "property_kick_landlord", [property], data.user) &&
      data.hasLandlord()
    ) {
      actions.push({
        id: "kick_landlord",
        title: t("main.removeAs") + " " + getSingularRoleTitle(Role.LANDLORD.id, undefined).toLowerCase(),
        iconColor: "var(--main-red-700)",
        iconName: "person_remove",
        requiredConsent: (datas) => {
          return new Promise((resolve) => {
            PopupTemplates.presentVerifyActionForDatas(
              t("main.confirmRemoveMemberAs") + " " + getSingularRoleTitle(Role.LANDLORD.id, undefined).toLowerCase(),
              this,
              datas.map((data) => data.property.name),
              () => {
                resolve({ dataObjects: datas });
              }
            );
          });
        },
        performAction: () => {
          return saveUpdate({
            roles: arrayRemove(1),
          });
        },
      });
    }
    if (ActionValidator.isAllowedToPerformAction("property_kick_tenant", [property], data.user) && data.hasTenant()) {
      actions.push({
        id: "kick_tenant",
        title: t("main.removeAs") + " " + getSingularRoleTitle(Role.RENTER.id, undefined).toLowerCase(),
        iconColor: "var(--main-red-700)",
        iconName: "person_remove",
        requiredConsent: (datas) => {
          return new Promise((resolve) => {
            PopupTemplates.presentVerifyActionForDatas(
              t("main.confirmRemoveMemberAs") + " " + getSingularRoleTitle(Role.RENTER.id, undefined).toLowerCase(),
              this,
              datas.map((data) => data.property.name),
              () => {
                resolve({ dataObjects: datas });
              }
            );
          });
        },
        performAction: () => {
          return saveUpdate({
            units: [],
          });
        },
      });
    }

    if (
      data.isAdmin &&
      ((!data.hasLandlord() && ActionValidator.isAllowedToPerformAction("property_remove_tenant_admin", [property])) ||
        ActionValidator.isAllowedToPerformAction("property_remove_admin", [property]))
    ) {
      actions.push({
        id: "remove_admin",
        title: t("main.removeAsSuperUser"),
        iconColor: "var(--main-red-700)",
        iconName: "remove_moderator",
        performAction: () => {
          return saveUpdate({
            admin: false,
          });
        },
      });
    }
    if (
      !data.isAdmin &&
      ((!data.hasLandlord() && ActionValidator.isAllowedToPerformAction("property_assign_tenant_admin", [property])) ||
        ActionValidator.isAllowedToPerformAction("property_assign_admin", [property]))
    ) {
      actions.push({
        id: "assign_admin",
        title: t("main.assignAsSuperUser"),
        iconColor: "var(--primary-color)",
        iconName: "admin_panel_settings",
        performAction: () => {
          return saveUpdate({
            admin: true,
          });
        },
      });
    }

    if (ActionValidator.isAllowedToPerformAction("property_administrate_operation_manager", [property])) {
      if (data.isOperationManager) {
        actions.push({
          id: "remove_operation_manager",
          title: t("main.removeAsOperationManager"),
          iconColor: "var(--main-red-700)",
          iconName: "admin_panel_settings",
          performAction: () => {
            return saveUpdate({
              operationManager: false,
            });
          },
        });
      } else {
        actions.push({
          id: "assign_operation_manager",
          title: t("main.assignAsOperationManager"),
          iconColor: "var(--primary-color)",
          iconName: "engineering",
          performAction: () => {
            return saveUpdate({
              operationManager: true,
            });
          },
        });
      }
    }

    if (roleIdentifier && ActionValidator.isAllowedToPerformAction("property_edit_visibility_" + roleIdentifier, [property])) {
      const settings = getCachedApiSettings({ propertyID: property.id });
      let visibilityType = getMemberVisibilityType(data, settings?.data);
      let title =
        visibilityType === "opt-out"
          ? data.visibility.hidden
            ? t("main.memberVisibility_removeHidden")
            : t("main.memberVisibility_makeHidden")
          : data.visibility.visible
            ? t("main.memberVisibility_removeVisible")
            : t("main.memberVisibility_makeVisible");
      actions.push({
        title: title,
        iconColor: "var(--primary-color)",
        iconName: !isMemberVisible(data, settings?.data) ? "visibility" : "visibility_off",
        id: isMemberVisible(data, settings?.data) ? "make_hidden" : "make_visible",
        performAction: () => {
          let newData: { [key: string]: any } = {};
          if (visibilityType === "opt-out") newData["hidden"] = !data.visibility.hidden;
          else if (visibilityType === "opt-in") newData["visible"] = !data.visibility.visible;

          return saveUpdate({
            visibility: newData,
          });
        },
      });
    }

    return actions;
  };

  getUniqueIDForData(data: MemberData): string {
    return data.getCompletePath();
  }
}
